'use client'

import {createContext, useContext, useEffect, useState, ReactNode} from 'react'

interface FullScreenContextType {
  isFullScreen: boolean
}

const FullScreenContext = createContext<FullScreenContextType>({
  isFullScreen: false,
})

interface FullScreenProviderProps {
  children: ReactNode
}

type FullScreenChangeCallbacks = {
  onFullScreenEnter?: () => void
  onFullScreenExit?: () => void
}

export const FullScreenProvider: React.FC<FullScreenProviderProps> = ({children}) => {
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    const handleFullScreenChange = () => {
      const fullscreenElement = document.fullscreenElement
      setIsFullScreen(!!fullscreenElement)
    }
    setIsFullScreen(!!document.fullscreenElement)

    // Add event listeners for all browser prefixes
    document.addEventListener('fullscreenchange', handleFullScreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange)
    document.addEventListener('mozfullscreenchange', handleFullScreenChange)
    document.addEventListener('MSFullscreenChange', handleFullScreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange)
    }
  }, [])

  return <FullScreenContext.Provider value={{isFullScreen}}>{children}</FullScreenContext.Provider>
}

export const useFullScreen = (): {isFullScreen: boolean} => {
  const context = useContext(FullScreenContext)

  if (context === undefined) {
    throw new Error('useFullScreen must be used within a FullScreenProvider')
  }

  return {isFullScreen: context.isFullScreen}
}
export const isCurrentlyInFullScreen = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  )
}

export const requestFullScreen = (element: HTMLElement = document.documentElement): void => {
  try {
    if (isCurrentlyInFullScreen()) {
      return
    }

    if (element.requestFullscreen) {
      element.requestFullscreen()
    } else if ((element as any).webkitRequestFullscreen) {
      ;(element as any).webkitRequestFullscreen()
    } else if ((element as any).mozRequestFullScreen) {
      ;(element as any).mozRequestFullScreen()
    } else if ((element as any).msRequestFullscreen) {
      ;(element as any).msRequestFullscreen()
    }
  } catch (error) {
    console.error('Error requesting fullscreen:', error)
  }
}

export const exitFullScreen = (): void => {
  try {
    if (!isCurrentlyInFullScreen()) {
      return
    }

    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if ((document as any).webkitExitFullscreen) {
      ;(document as any).webkitExitFullscreen()
    } else if ((document as any).mozCancelFullScreen) {
      ;(document as any).mozCancelFullScreen()
    } else if ((document as any).msExitFullscreen) {
      ;(document as any).msExitFullscreen()
    }
  } catch (error) {
    console.error('Error exiting fullscreen:', error)
  }
}

export const useFullScreenChange = ({onFullScreenEnter, onFullScreenExit}: FullScreenChangeCallbacks) => {
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isFullScreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      )

      if (isFullScreen) {
        onFullScreenEnter?.()
      } else {
        onFullScreenExit?.()
      }
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange)
    document.addEventListener('mozfullscreenchange', handleFullScreenChange)
    document.addEventListener('MSFullscreenChange', handleFullScreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange)
    }
  }, [onFullScreenEnter, onFullScreenExit])
}
